/**
 * WizardPreviewPanel.jsx
 *
 * Renders a read-only "dry run" of a remediation wizard fetched from
 * GET /api/EndpointHealth/wizards/{id}/preview (WizardPreview/StepPreview).
 *
 * Shows, for the current environment, whether the wizard would dispatch live or be
 * simulated, plus a human-readable description of exactly what each step will run,
 * the external capability it uses, its run condition, and its retry/timeout settings.
 *
 * No side effects — purely informational so operators can see what a wizard does
 * before triggering it.
 *
 * @module SettingsPages/components/remediation/WizardPreviewPanel
 */

import {
    FaSearch, FaSpinner, FaBolt, FaShieldAlt, FaClock, FaRedo, FaTimes
} from 'react-icons/fa';

const modeClass = (mode) => {
    switch ((mode || '').toLowerCase()) {
        case 'live': return 'mode-live';
        case 'disabled': return 'mode-disabled';
        default: return 'mode-simulated';
    }
};

const WizardPreviewPanel = ({ preview, loading, error, onClose }) => {
    return (
        <div className="wizard-preview-panel">
            <div className="preview-head">
                <h4><FaSearch /> Dry-Run Preview</h4>
                {onClose && (
                    <button type="button" className="btn-icon" title="Close preview" onClick={onClose}>
                        <FaTimes />
                    </button>
                )}
            </div>

            {loading && (
                <div className="preview-mode-banner mode-simulated">
                    <FaSpinner className="spin" /> Building preview&hellip;
                </div>
            )}

            {error && !loading && (
                <div className="preview-mode-banner mode-disabled">{error}</div>
            )}

            {preview && !loading && !error && (
                <>
                    <div className={`preview-mode-banner ${modeClass(preview.executionMode)}`}>
                        <FaBolt />
                        <span>
                            <strong>{preview.executionMode}</strong>
                            {' '}in <strong>{preview.environmentName}</strong>
                            {preview.executionModeReason ? ` — ${preview.executionModeReason}` : ''}
                        </span>
                    </div>

                    <ol className="preview-steps">
                        {(preview.steps || []).map((s) => (
                            <li className="preview-step" key={s.stepId || s.order}>
                                <span className="preview-step-order">{s.order}</span>
                                <div className="preview-step-main">
                                    <div className="preview-step-name">{s.name}</div>
                                    <div className="preview-step-desc">{s.description}</div>
                                    <div className="preview-step-meta">
                                        {s.requiresExternalAction ? (
                                            <span className={`meta-chip ${s.willRunLive ? 'live' : 'simulated'}`}>
                                                <FaShieldAlt size={10} /> {s.capability}{s.willRunLive ? ' · Live' : ' · Simulated'}
                                            </span>
                                        ) : (
                                            <span className="meta-chip">No side effect</span>
                                        )}
                                        {s.conditionSummary && (
                                            <span className="meta-chip condition">{s.conditionSummary}</span>
                                        )}
                                        {s.retryCount > 0 && (
                                            <span className="meta-chip"><FaRedo size={10} /> {s.retryCount} retr{s.retryCount === 1 ? 'y' : 'ies'}</span>
                                        )}
                                        {s.timeoutSeconds > 0 && (
                                            <span className="meta-chip"><FaClock size={10} /> {s.timeoutSeconds}s timeout</span>
                                        )}
                                        {s.continueOnFailure && (
                                            <span className="meta-chip">Continues on failure</span>
                                        )}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ol>

                    {(preview.steps || []).length === 0 && (
                        <div className="preview-mode-banner mode-disabled">
                            This wizard has no active steps to preview.
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default WizardPreviewPanel;
